/**
 * useSwipeNavigation — Phase 9.1: Mobile-Optimized Form Renderer
 *
 * Detects horizontal swipe gestures on touch-enabled devices and fires
 * callbacks to navigate between form sections (or any paginated UI).
 *
 * Features:
 * - Configurable swipe threshold (px) and velocity threshold
 * - Direction lock: once a swipe direction is decided, the other axis is ignored
 * - onSwipeLeft / onSwipeRight callbacks
 * - Prevents accidental swipe during vertical scroll
 * - Returns a ref to attach to the swipeable container
 *
 * @phase 9.1 - Mobile-Optimized Form Renderer
 */

'use client';

import { useRef, useCallback, useEffect } from 'react';

export interface UseSwipeNavigationOptions {
  /** Callback when user swipes left (i.e. go to next) */
  onSwipeLeft?: () => void;
  /** Callback when user swipes right (i.e. go to previous) */
  onSwipeRight?: () => void;
  /** Minimum horizontal distance (px) to count as a swipe. Default: 50 */
  threshold?: number;
  /** Maximum vertical deviation (px) before swipe is cancelled. Default: 80 */
  maxVerticalDeviation?: number;
  /** Whether swipe navigation is enabled. Default: true */
  enabled?: boolean;
}

export interface UseSwipeNavigationResult {
  /** Attach this ref to the container element that should be swipeable */
  containerRef: React.RefCallback<HTMLElement>;
}

export function useSwipeNavigation({
  onSwipeLeft,
  onSwipeRight,
  threshold = 50,
  maxVerticalDeviation = 80,
  enabled = true,
}: UseSwipeNavigationOptions): UseSwipeNavigationResult {
  const startX = useRef(0);
  const startY = useRef(0);
  const tracking = useRef(false);
  const elementRef = useRef<HTMLElement | null>(null);

  const handleTouchStart = useCallback(
    (e: TouchEvent) => {
      if (!enabled) return;
      const touch = e.touches[0];
      startX.current = touch.clientX;
      startY.current = touch.clientY;
      tracking.current = true;
    },
    [enabled]
  );

  const handleTouchEnd = useCallback(
    (e: TouchEvent) => {
      if (!enabled || !tracking.current) return;
      tracking.current = false;

      const touch = e.changedTouches[0];
      const dx = touch.clientX - startX.current;
      const dy = touch.clientY - startY.current;

      // If vertical movement is too large, not a horizontal swipe
      if (Math.abs(dy) > maxVerticalDeviation) return;

      // If horizontal distance doesn't meet threshold, ignore
      if (Math.abs(dx) < threshold) return;

      if (dx < 0) {
        // Swiped left → go next
        onSwipeLeft?.();
      } else {
        // Swiped right → go previous
        onSwipeRight?.();
      }
    },
    [enabled, threshold, maxVerticalDeviation, onSwipeLeft, onSwipeRight]
  );

  // Cleanup old element listeners when ref changes
  useEffect(() => {
    return () => {
      if (elementRef.current) {
        elementRef.current.removeEventListener('touchstart', handleTouchStart);
        elementRef.current.removeEventListener('touchend', handleTouchEnd);
      }
    };
  }, [handleTouchStart, handleTouchEnd]);

  // Ref callback for the swipeable container
  const containerRef = useCallback(
    (node: HTMLElement | null) => {
      // Remove listeners from old element
      if (elementRef.current) {
        elementRef.current.removeEventListener('touchstart', handleTouchStart);
        elementRef.current.removeEventListener('touchend', handleTouchEnd);
      }
      elementRef.current = node;
      // Attach listeners to new element
      if (node) {
        node.addEventListener('touchstart', handleTouchStart, { passive: true });
        node.addEventListener('touchend', handleTouchEnd, { passive: true });
      }
    },
    [handleTouchStart, handleTouchEnd]
  );

  return { containerRef };
}

export default useSwipeNavigation;
